import React from 'react';
import { Box, Card, CardContent, Typography, Grid, CircularProgress } from '@mui/material';
import Chart from 'react-apexcharts';
import useElementWidth from '../../hooks/useElementWidth';

const EmployeeDashboardCharts = ({ attendanceStats, leaveStats, isLoading }) => {
  const [lineChartWrapRef, lineChartWidth] = useElementWidth();
  const [pieChartWrapRef, pieChartWidth] = useElementWidth();
  const monthlyTrend = Array.isArray(attendanceStats?.monthly_trend) ? attendanceStats.monthly_trend : [];
  const normalizedTrend = monthlyTrend
    .map((item) => ({
      month: item?.month ?? '',
      percentage: Number(item?.percentage),
    }))
    .filter((item) => item.month && Number.isFinite(item.percentage));

  const trendCategories = normalizedTrend.map((item) => item.month);
  const trendData = normalizedTrend.map((item) => item.percentage);
  const hasSingleTrendPoint = trendData.length === 1;
  const hasTrendData = trendData.length > 0;

  // Calculate min and max for y-axis with some padding
  const minPercentage = trendData.length > 0 ? Math.max(0, Math.floor(Math.min(...trendData)) - 5) : 0;
  const maxPercentage = trendData.length > 0 ? Math.min(100, Math.ceil(Math.max(...trendData)) + 5) : 100;

  const attendanceTrendOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
    },
    stroke: {
      curve: 'smooth',
      width: hasSingleTrendPoint ? 0 : 3,
    },
    dataLabels: {
      enabled: hasSingleTrendPoint,
      formatter: (val) => `${Number(val).toFixed(0)}%`,
      offsetY: -8,
    },
    xaxis: {
      categories: trendCategories.length > 0 ? trendCategories : ['No Data'],
    },
    yaxis: {
      min: minPercentage,
      max: maxPercentage,
      title: {
        text: 'Attendance %',
      },
    },
    colors: ['#0A9EF2'],
    grid: {
      borderColor: '#f1f1f1',
    },
    markers: {
      size: hasSingleTrendPoint ? 9 : 6,
      strokeWidth: 2,
      fillOpacity: 1,
    },
    tooltip: {
      y: {
        formatter: (val) => `${val.toFixed(2)}%`,
      },
    },
  };

  const attendanceTrendSeries = [
    {
      name: 'Attendance',
      data: hasTrendData ? trendData : [0],
    },
  ];

  const leaveStatusOptions = {
    chart: {
      type: 'pie',
      toolbar: { show: false },
    },
    labels: ['Approved', 'Pending', 'Rejected'],
    colors: ['#051092', '#0A9EF2', '#5E69F4'],
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(0)}%`,
    },
    legend: {
      position: 'bottom',
    },
  };

  const leaveStatusSeries = [
    leaveStats?.approved_leaves || 0,
    leaveStats?.pending_leaves || 0,
    leaveStats?.rejected_leaves || 0,
  ];
  const hasLeaveData = leaveStatusSeries.some((n) => Number(n) > 0);

  const cardSx = { height: '100%', width: '100%', display: 'flex', flexDirection: 'column' };
  const contentSx = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '&:last-child': { pb: 2 },
  };

  const ChartLoader = ({ minHeight = 300 }) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <CircularProgress size={32} />
    </Box>
  );

  const EmptyState = ({ title = 'No data yet', subtitle = 'Try adjusting the filters or date range.', minHeight = 300 }) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 360 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Grid container spacing={3} sx={{ marginTop: 0 }}>
      <Grid item xs={12} lg={6} sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Card sx={cardSx}>
          <CardContent sx={contentSx}>
            <Typography variant="h6" gutterBottom>
              Attendance Trend
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Monthly attendance percentage
            </Typography>
            {isLoading ? (
              <ChartLoader minHeight={300} />
            ) : hasTrendData !== true ? (
              <EmptyState
                title="No attendance trend data"
                subtitle="There isn’t enough data in this range to plot a trend."
                minHeight={300}
              />
            ) : (
              <Box
                ref={lineChartWrapRef}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 300,
                  width: '100%',
                }}
              >
                {lineChartWidth > 0 ? (
                  <Chart
                    options={attendanceTrendOptions}
                    series={attendanceTrendSeries}
                    type="line"
                    height={300}
                    width={lineChartWidth}
                  />
                ) : (
                  <ChartLoader minHeight={300} />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} lg={6} sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Card sx={cardSx}>
          <CardContent sx={contentSx}>
            <Typography variant="h6" gutterBottom>
              Leave Status
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Distribution of leave requests
            </Typography>
            {isLoading ? (
              <ChartLoader minHeight={300} />
            ) : hasLeaveData !== true ? (
              <EmptyState title="No leave requests" subtitle="No leave records were found for this selection." minHeight={300} />
            ) : (
              <Box
                ref={pieChartWrapRef}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 300,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {pieChartWidth > 0 ? (
                  <Chart
                    options={leaveStatusOptions}
                    series={leaveStatusSeries}
                    type="pie"
                    height={300}
                    width={pieChartWidth}
                  />
                ) : (
                  <ChartLoader minHeight={300} />
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default EmployeeDashboardCharts;
